import * as React from "react"
import { ChevronLeft, ChevronRight, DownloadIcon, Loader2, Maximize2, XIcon } from "lucide-react"
import { Dialog as DialogPrimitive } from "radix-ui"
import { useTranslation } from "react-i18next"
import { cn } from "../../../lib/utils"
import {
  useOverlayLock,
  guardWhenLocked,
  OVERLAY_LOCKED_CLASSES,
} from "../../../providers/overlay-lock-provider"

// A hover must dwell this long before the preview <video> mounts. Sweeping the
// pointer across a dense grid would otherwise spin up a full-resolution decoder
// for every card passed over — Chrome reclaims that GPU/decoder memory only
// slowly after unmount, so the footprint climbs during a fast scan. Only a
// deliberate hover crosses the threshold.
const HOVER_DWELL_MS = 200

/** Meta feed placement (1080×1350). Shared by every Nexus creative card and drawer. */
export const FACEBOOK_FEED_ASPECT_CLASS = "aspect-[4/5]"

/** A single slide in a carousel. */
export interface MediaSlide {
  assetUrl: string
  thumbnailUrl: string
  type: "image" | "video"
}

export interface MediaPreviewProps {
  /** Full-quality media URL opened in the lightbox (ignored when `slides` is provided) */
  src?: string
  /** Thumbnail image shown in the card */
  thumbnailSrc: string
  type: "video" | "image"
  alt?: string
  /** Title shown in the lightbox footer */
  title?: string
  /** Logo URL shown next to the title in the lightbox footer */
  logoSrc?: string
  /** Metadata rendered in the lightbox footer (badges, stats, etc.) */
  meta?: React.ReactNode
  /** Classes for the outer container — pass aspect ratio, rounding, overflow here */
  className?: string
  /** Overlay children rendered inside the container (badges, brand strip, etc.) */
  children?: React.ReactNode
  /**
   * When provided, the lightbox opens in carousel mode. Navigation arrows and a
   * slide counter are added; the `src` prop is ignored inside the lightbox.
   */
  slides?: MediaSlide[]
  /**
   * What opens the lightbox. `thumbnail` (default): clicking the media. `button`: a
   * small corner button, leaving media clicks to the surrounding card.
   */
  previewTrigger?: "thumbnail" | "button"
  /** Plays a muted, looping preview of a video while the card is hovered. Defaults to true. */
  hoverPlayback?: boolean
  /** Starts the lightbox video as soon as it opens. Defaults to true; false waits for the viewer to press play. */
  autoPlay?: boolean
}

function BrokenImageIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className={className}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3 3l18 18M9 9.75A2.25 2.25 0 1111.25 12 2.25 2.25 0 019 9.75z"
      />
    </svg>
  )
}

export function MediaPreview({
  src,
  thumbnailSrc,
  type,
  alt = "",
  title,
  logoSrc,
  meta,
  className,
  children,
  slides,
  previewTrigger = "thumbnail",
  hoverPlayback = true,
  autoPlay = true,
}: MediaPreviewProps) {
  const { t } = useTranslation()
  const isLocked = useOverlayLock()
  const [open, setOpen] = React.useState(false)
  const [hovering, setHovering] = React.useState(false)
  const videoRef = React.useRef<HTMLVideoElement>(null)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const hoverTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  // Thumbnail states
  const [thumbLoaded, setThumbLoaded] = React.useState(false)
  const [thumbError, setThumbError] = React.useState(false)
  // Natural aspect ratio of thumbnail — used to pre-size lightbox before full image arrives
  const [thumbRatio, setThumbRatio] = React.useState<number | null>(null)

  // Lightbox media states — reset each time the dialog opens or slide changes
  const [mediaLoading, setMediaLoading] = React.useState(false)
  const [mediaError, setMediaError] = React.useState(false)
  // Falls back to thumbnailSrc when src fails — lower quality but better than broken image
  const [lightboxSrc, setLightboxSrc] = React.useState(src)
  const [downloading, setDownloading] = React.useState(false)
  const lightboxImgRef = React.useRef<HTMLImageElement>(null)
  const lightboxVideoRef = React.useRef<HTMLVideoElement>(null)

  // Carousel state
  const [currentSlideIndex, setCurrentSlideIndex] = React.useState(0)
  // Direction of the most recent navigation — drives the slide-in CSS animation
  const [slideDir, setSlideDir] = React.useState<"next" | "prev" | null>(null)
  // When a carousel video slide fails to load as .mp4, we fall back to .png silently
  const [mediaFallback, setMediaFallback] = React.useState(false)
  // Ref lets goToSlide read the current index without adding it to its dep array
  const currentSlideIndexRef = React.useRef(currentSlideIndex)
  currentSlideIndexRef.current = currentSlideIndex
  // Ref for the active thumbnail so we can scroll it into view after navigation
  const activeThumbnailRef = React.useRef<HTMLButtonElement>(null)
  // Ref on the slide media wrapper — used to retrigger CSS animation without remounting
  const mediaWrapperRef = React.useRef<HTMLDivElement>(null)

  const isCarousel = Boolean(slides?.length)
  const isInteractive = isCarousel || Boolean(src)
  const opensFromThumbnail = isInteractive && previewTrigger === "thumbnail"
  const opensFromButton = isInteractive && previewTrigger === "button"
  const previewName = alt || title || t("common.mediaPreview.previewFallback")

  // Derived active-slide values — override the component-level props inside the lightbox
  const activeSlide = slides?.[currentSlideIndex]
  // When a carousel slide fails, try the other type: image → video, video → image
  const fallbackType: "image" | "video" = activeSlide?.type === "image" ? "video" : "image"
  const activeType = mediaFallback ? fallbackType : (activeSlide?.type ?? type)
  const activeThumbnail = activeSlide?.thumbnailUrl ?? thumbnailSrc
  // Swap the file extension to match the fallback type
  const effectiveLightboxSrc = mediaFallback
    ? (lightboxSrc?.replace(
        activeSlide?.type === "image" ? /\.png$/ : /\.mp4$/,
        activeSlide?.type === "image" ? ".mp4" : ".png",
      ) ?? "")
    : lightboxSrc

  // Cached images never re-fire onLoad — check .complete after dialog opens
  React.useEffect(() => {
    if (!open || activeType !== "image") return
    const img = lightboxImgRef.current
    if (img?.complete) setMediaLoading(false)
  }, [open, activeType])

  // Drop hover playback when the card scrolls out of the viewport. The hover <video>
  // is only mounted while `hovering` is true (see render), so clearing it unmounts the
  // element entirely — releasing its decoded frames and network buffers. This is what
  // keeps memory bounded in long infinite-scroll creative grids, where dozens of cards
  // would otherwise each retain a live media element. Pausing/calling load() on an
  // element that stays attached to the DOM (the previous approach) does not release it.
  React.useEffect(() => {
    if (type !== "video" || !src) return
    const el = containerRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) setHovering(false)
      },
      { rootMargin: "100px 0px" },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [type, src])

  // Start playback once the hover <video> mounts. The element does not exist until
  // `hovering` flips true, so play() runs from an effect after commit rather than
  // synchronously in the mouse-enter handler (where the ref would still be null).
  React.useEffect(() => {
    if (type !== "video" || !hovering) return
    videoRef.current?.play().catch(() => {})
  }, [type, hovering])

  const goToSlide = React.useCallback((index: number) => {
    if (!slides) return
    setSlideDir(currentSlideIndexRef.current < index ? "next" : "prev")
    // Release the current video's decoded frames before switching
    const lv = lightboxVideoRef.current
    if (lv) { lv.pause(); lv.load() }
    setCurrentSlideIndex(index)
    setMediaLoading(true)
    setMediaError(false)
    setMediaFallback(false)
    setLightboxSrc(slides[index]!.assetUrl)
  }, [slides])

  const handleOpenChange = React.useCallback((val: boolean) => {
    if (val) {
      setCurrentSlideIndex(0)
      setSlideDir(null)
      setMediaFallback(false)
      setMediaLoading(true)
      setMediaError(false)
      setLightboxSrc(isCarousel ? slides![0]!.assetUrl : src)
      setDownloading(false)
    } else {
      const lv = lightboxVideoRef.current
      if (lv) { lv.pause(); lv.load() }
    }
    setOpen(val)
  }, [src, slides, isCarousel])

  // Keyboard navigation for carousel — skip while overlay-locked (offline).
  React.useEffect(() => {
    if (!open || !isCarousel || !slides || isLocked) return
    function handleKey(e: KeyboardEvent) {
      if (e.key === "ArrowLeft" && currentSlideIndex > 0) goToSlide(currentSlideIndex - 1)
      if (e.key === "ArrowRight" && currentSlideIndex < slides!.length - 1) goToSlide(currentSlideIndex + 1)
    }
    document.addEventListener("keydown", handleKey)
    return () => document.removeEventListener("keydown", handleKey)
  }, [open, isCarousel, slides, currentSlideIndex, goToSlide, isLocked])

  // Preload all carousel slide assets when the lightbox opens so navigation is instant.
  // Image objects are kept alive in the closure until the effect re-runs or cleans up;
  // the browser fetches and caches each URL so subsequent onLoad calls fire synchronously.
  React.useEffect(() => {
    if (!open || !isCarousel || !slides) return
    const imgs: HTMLImageElement[] = slides.flatMap((slide) => {
      const nodes: HTMLImageElement[] = []
      // Thumbnail — shown in the strip and used as video poster
      const thumb = new Image()
      thumb.src = slide.thumbnailUrl
      nodes.push(thumb)
      // Full-quality image asset (image slides only; video preloading is handled by the browser natively)
      if (slide.type === "image") {
        const full = new Image()
        full.src = slide.assetUrl
        nodes.push(full)
      }
      return nodes
    })
    return () => { imgs.length = 0 }
  }, [open, isCarousel, slides])

  // Scroll the active thumbnail into view after each navigation
  React.useEffect(() => {
    if (!open) return
    activeThumbnailRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" })
  }, [currentSlideIndex, open])

  // Retrigger the slide-in CSS animation without remounting the wrapper element.
  // void el.offsetHeight forces a reflow so the browser treats the re-assigned
  // animation as a fresh start even though the DOM node is the same one.
  React.useEffect(() => {
    const el = mediaWrapperRef.current
    if (!el || !slideDir) return
    el.style.animation = "none"
    void el.offsetHeight
    el.style.animation = `${slideDir === "next" ? "_mp_slide_r" : "_mp_slide_l"} 220ms cubic-bezier(0.25,0.1,0.25,1) both`
  }, [currentSlideIndex, slideDir])

  const handleDownload = React.useCallback(async () => {
    if (!effectiveLightboxSrc || downloading) return
    setDownloading(true)
    try {
      const res = await fetch(effectiveLightboxSrc)
      if (!res.ok) throw new Error(`${res.status}`)
      const blob = await res.blob()
      const blobUrl = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = blobUrl
      a.download = effectiveLightboxSrc.split("/").pop()?.split("?")[0] ?? "download"
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(blobUrl)
    } catch {
      // fallback: open in new tab
      window.open(effectiveLightboxSrc, "_blank", "noopener,noreferrer")
    } finally {
      setDownloading(false)
    }
  }, [effectiveLightboxSrc, downloading])

  const handleMouseEnter = React.useCallback(() => {
    if (type !== "video" || !src || thumbError || !hoverPlayback) return
    // Arm the dwell timer instead of mounting immediately — a transient
    // pass-through hover is cancelled in handleMouseLeave before it fires.
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current)
    hoverTimerRef.current = setTimeout(() => setHovering(true), HOVER_DWELL_MS)
  }, [type, src, thumbError, hoverPlayback])

  const handleMouseLeave = React.useCallback(() => {
    if (type !== "video") return
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current)
      hoverTimerRef.current = null
    }
    setHovering(false)
  }, [type])

  // Clear a pending dwell timer if the card unmounts mid-hover (virtualization
  // recycle, tab switch, drawer close) so it can't fire setHovering after unmount.
  React.useEffect(() => () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current)
  }, [])

  const handleClick = React.useCallback(() => {
    if (opensFromThumbnail) handleOpenChange(true)
  }, [opensFromThumbnail, handleOpenChange])

  const handleKeyDown = React.useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      if (opensFromThumbnail) handleOpenChange(true)
    }
  }, [opensFromThumbnail, handleOpenChange])

  const handlePreviewButtonClick = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    handleOpenChange(true)
  }, [handleOpenChange])

  return (
    <>
      {/* ── Card thumbnail area ── */}
      <div
        ref={containerRef}
        className={cn("relative", opensFromThumbnail && "cursor-pointer", className)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={opensFromThumbnail ? handleClick : undefined}
        onKeyDown={opensFromThumbnail ? handleKeyDown : undefined}
        role={opensFromThumbnail ? "button" : undefined}
        tabIndex={opensFromThumbnail ? 0 : undefined}
        aria-label={opensFromThumbnail ? `Preview ${previewName}` : undefined}
      >
        {/* Loading shimmer — visible until thumbnail resolves */}
        {!thumbLoaded && !thumbError ? (
          <div className="absolute inset-0 animate-pulse bg-muted" aria-hidden="true" />
        ) : null}

        {/* Error fallback */}
        {thumbError ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-muted">
            <BrokenImageIcon className="size-8 text-muted-foreground/40" />
          </div>
        ) : (
          <img
            src={thumbnailSrc}
            alt={alt}
            loading="lazy"
            decoding="async"
            className={cn(
              "size-full object-contain transition-opacity duration-300",
              !thumbLoaded ? "opacity-0" : "opacity-100",
              hovering && "opacity-0",
            )}
            onLoad={(e) => {
              setThumbLoaded(true)
              const { naturalWidth: w, naturalHeight: h } = e.currentTarget
              if (w && h) setThumbRatio(w / h)
            }}
            onError={() => { setThumbLoaded(true); setThumbError(true) }}
          />
        )}

        {/* Hover video preview — mounted only while hovering so non-hovered and
            off-screen cards hold no <video> element (and no decoded frames) in memory.
            `poster` shows the thumbnail until the first frame paints, avoiding a flash. */}
        {type === "video" && src && !thumbError && hovering ? (
          <video
            ref={videoRef}
            src={src}
            poster={thumbnailSrc}
            muted
            loop
            playsInline
            preload="none"
            className="absolute inset-0 size-full object-contain"
          />
        ) : null}

        {/* Overlay children (badges, brand strip, etc.) */}
        {children}

        {opensFromButton ? (
          <button
            type="button"
            onClick={handlePreviewButtonClick}
            aria-label={t("common.mediaPreview.openPreview", {
              name: previewName,
              defaultValue: "Preview {{name}}",
            })}
            className="absolute bottom-1.5 right-1.5 flex size-6 items-center justify-center rounded-full bg-white/90 text-gray-700 shadow-sm transition-colors hover:text-foreground dark:bg-black/60 dark:text-gray-200"
          >
            <Maximize2 className="size-3" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      {/* ── Lightbox ── */}
      {isInteractive ? (
        <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
          <DialogPrimitive.Portal>
            <DialogPrimitive.Overlay
              className={cn(
                "fixed inset-0 z-(--z-modal) bg-black/85",
                "data-[state=open]:animate-in data-[state=open]:fade-in-0",
                "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
              )}
            />
            <DialogPrimitive.Content
              aria-describedby={undefined}
              inert={isLocked || undefined}
              aria-busy={isLocked || undefined}
              onEscapeKeyDown={guardWhenLocked(isLocked)}
              onInteractOutside={guardWhenLocked(isLocked)}
              className={cn(
                "fixed left-1/2 top-1/2 z-(--z-modal) flex -translate-x-1/2 -translate-y-1/2 flex-col",
                "overflow-hidden rounded-xl bg-[#0f0f0f] shadow-2xl outline-none",
                "max-h-[92dvh] w-auto max-w-[92dvw]",
                "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
                "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
                "duration-200",
                isLocked && OVERLAY_LOCKED_CLASSES,
              )}
            >
              <DialogPrimitive.Title className="sr-only">
                {title ?? t("common.mediaPreview.titleFallback")}
              </DialogPrimitive.Title>

              {/* Keyframes — loading bar sweep + carousel slide-in animations */}
              <style>{`@keyframes _mp_sweep{from{transform:translateX(-100%)}to{transform:translateX(400%)}}@keyframes _mp_slide_r{from{transform:translateX(40px);opacity:0}to{transform:translateX(0);opacity:1}}@keyframes _mp_slide_l{from{transform:translateX(-40px);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>

              {/* Indeterminate loading bar */}
              {!mediaError && mediaLoading ? (
                <div className="absolute inset-x-0 top-0 z-20 h-1 overflow-hidden bg-white/15" aria-hidden="true">
                  <div
                    className="h-full w-1/4 bg-white"
                    style={{ animation: "_mp_sweep 0.9s linear infinite" }}
                  />
                </div>
              ) : null}

              {/* Close button */}
              <DialogPrimitive.Close className="absolute right-3 top-3 z-10 flex size-8 items-center justify-center rounded-full bg-black/60 text-white/70 backdrop-blur-sm transition-colors hover:bg-black/80 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30">
                <XIcon className="size-4" />
                <span className="sr-only">{t("common.mediaPreview.close")}</span>
              </DialogPrimitive.Close>

              {/* Carousel nav arrows */}
              {isCarousel && slides ? (
                <>
                  <button
                    type="button"
                    onClick={() => goToSlide(currentSlideIndex - 1)}
                    disabled={currentSlideIndex === 0}
                    className="absolute left-3 top-1/2 z-10 flex size-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white/70 backdrop-blur-sm transition-colors hover:bg-black/80 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 disabled:opacity-30 disabled:cursor-default"
                    aria-label={t("common.mediaPreview.previousSlide", "Previous slide")}
                  >
                    <ChevronLeft className="size-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => goToSlide(currentSlideIndex + 1)}
                    disabled={currentSlideIndex === slides.length - 1}
                    className="absolute right-3 top-1/2 z-10 flex size-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white/70 backdrop-blur-sm transition-colors hover:bg-black/80 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 disabled:opacity-30 disabled:cursor-default"
                    aria-label={t("common.mediaPreview.nextSlide", "Next slide")}
                  >
                    <ChevronRight className="size-4" />
                  </button>
                </>
              ) : null}

              {/* Error */}
              {mediaError ? (
                <div className="flex h-52 min-w-72 flex-col items-center justify-center gap-3">
                  <BrokenImageIcon className="size-10 text-white/25" />
                  <p className="text-center text-sm text-white/40">{t("common.mediaPreview.failedToLoad")}</p>
                </div>
              ) : null}

              {/* Media wrapper — animation is retriggered via ref+reflow in a useEffect so
                  the element is never remounted (which would cause a blank-frame flicker). */}
              {!mediaError ? (
                <div ref={mediaWrapperRef} className="flex w-full justify-center">
                  {/* Image — blur-up: container pre-sized from thumbnail ratio so no jump on load */}
                  {activeType === "image" ? (
                    <div
                      className="relative flex items-center justify-center overflow-hidden"
                      style={
                        thumbRatio
                          ? {
                              aspectRatio: String(thumbRatio),
                              height: `min(80dvh, calc(88dvw / ${thumbRatio}))`,
                            }
                          : { minHeight: "13rem", minWidth: "18rem" }
                      }
                    >
                      {/* Blurred thumbnail overlay — fills container while full image fetches */}
                      {mediaLoading ? (
                        <img
                          src={activeThumbnail}
                          aria-hidden="true"
                          alt=""
                          className="absolute inset-0 size-full object-cover blur-sm"
                        />
                      ) : null}

                      {/* Full quality image — centered, fades in when ready.
                          No key: same element persists so the old image stays visible
                          while the new src loads, eliminating the blank-frame flicker. */}
                      <img
                        ref={lightboxImgRef}
                        src={effectiveLightboxSrc}
                        alt={alt}
                        className={cn(
                          "max-h-[80dvh] max-w-[88dvw]",
                          // transition only on the reveal, not the hide — instant hide means
                          // the new slide's blurred thumbnail shows immediately on navigation
                          mediaLoading ? "opacity-0" : "opacity-100 transition-opacity duration-500",
                        )}
                        onLoad={() => setMediaLoading(false)}
                        onError={() => {
                          // Carousel image slides: silently try .mp4 before showing error
                          if (isCarousel && activeSlide?.type === "image" && !mediaFallback) {
                            setMediaFallback(true)
                            setMediaLoading(true)
                          } else if (effectiveLightboxSrc === src && thumbnailSrc && thumbnailSrc !== src) {
                            // Non-carousel: fall back to thumbnail rather than showing broken image
                            setLightboxSrc(thumbnailSrc)
                          } else {
                            setMediaLoading(false)
                            setMediaError(true)
                          }
                        }}
                      />
                    </div>
                  ) : null}

                  {/* Video — poster thumbnail shown natively while buffering */}
                  {activeType === "video" ? (
                    <video
                      ref={lightboxVideoRef}
                      key={effectiveLightboxSrc}
                      src={effectiveLightboxSrc}
                      poster={activeThumbnail}
                      controls
                      autoPlay={autoPlay}
                      playsInline
                      className="max-h-[80dvh] w-auto max-w-[88dvw] min-w-72 bg-black"
                      onCanPlay={() => setMediaLoading(false)}
                      onError={() => {
                        // Carousel video slides: silently fall back to .png rather than showing error state
                        if (isCarousel && activeSlide?.type === "video" && !mediaFallback) {
                          setMediaFallback(true)
                          setMediaLoading(true)
                        } else {
                          setMediaLoading(false)
                          setMediaError(true)
                        }
                      }}
                    />
                  ) : null}
                </div>
              ) : null}

              {/* Carousel thumbnail strip */}
              {isCarousel && slides && slides.length > 1 ? (
                <div className="flex justify-center gap-1.5 overflow-x-auto px-4 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  {slides.map((s, i) => (
                    <button
                      key={s.assetUrl}
                      ref={i === currentSlideIndex ? activeThumbnailRef : undefined}
                      type="button"
                      onClick={() => goToSlide(i)}
                      className={cn(
                        "shrink-0 overflow-hidden rounded transition-all duration-150",
                        i === currentSlideIndex
                          ? "ring-2 ring-white opacity-100"
                          : "opacity-40 hover:opacity-70",
                      )}
                      aria-label={t("common.mediaPreview.goToSlide", "Go to slide {{n}}", { n: i + 1 })}
                      aria-current={i === currentSlideIndex ? "true" : undefined}
                    >
                      <img
                        src={s.thumbnailUrl}
                        alt={`Slide ${i + 1}`}
                        className="size-11 object-cover"
                      />
                    </button>
                  ))}
                </div>
              ) : null}

              {/* Footer — `w-0 min-w-full` keeps the row out of the dialog's
                  shrink-to-fit width calculation: titles are often long unbroken
                  asset URLs whose max-content width would stretch the lightbox far
                  past the media. The percentage min-width resolves only after the
                  dialog width is settled by the media, so the row still fills it. */}
              <div className="flex w-0 min-w-full items-center justify-between gap-3 px-4 py-3">
                {/* Left: logo + title + meta */}
                <div className="flex min-w-0 items-center gap-3">
                  {logoSrc ? (
                    <img
                      src={logoSrc}
                      alt=""
                      aria-hidden="true"
                      className="size-7 shrink-0 rounded-full object-cover ring-1 ring-white/20"
                    />
                  ) : null}
                  <div className="min-w-0">
                    {title ? (
                      <p className="truncate text-sm font-semibold text-white">{title}</p>
                    ) : null}
                    {meta ? (
                      <div className="mt-0.5 flex items-center gap-2 text-xs text-white/50">
                        {meta}
                      </div>
                    ) : null}
                  </div>
                </div>
                {/* Right: slide counter (carousel) + download */}
                <div className="flex shrink-0 items-center gap-2">
                  {isCarousel && slides ? (
                    <span className="tabular-nums text-xs text-white/50">
                      {currentSlideIndex + 1} / {slides.length}
                    </span>
                  ) : null}
                  {effectiveLightboxSrc && !mediaError ? (
                    <button
                      type="button"
                      onClick={handleDownload}
                      disabled={downloading}
                      className="flex shrink-0 items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-xs font-medium text-white/80 transition-colors hover:bg-white/20 hover:text-white disabled:opacity-50"
                      aria-label={t("common.mediaPreview.download")}
                    >
                      {downloading
                        ? <Loader2 className="size-3.5 animate-spin" />
                        : <DownloadIcon className="size-3.5" />
                      }
                      {downloading ? t("common.mediaPreview.downloading") : t("common.mediaPreview.download")}
                    </button>
                  ) : null}
                </div>
              </div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
      ) : null}
    </>
  )
}
